import type { PersonId } from '@shared/brand';
import { err, ok, type Result } from '@shared/result';
import { availableDays } from '@domain/entities/Balance';
import { requiredSignaturesFor, type SignatureCount } from '@domain/policies/ApprovalPolicy';
import {
  allocateAcrossPeriods,
  livePeriodsBetween,
  livePeriodsOn,
  periodFor,
  validUntil,
} from '@domain/policies/VacationPeriodPolicy';
import { canAfford, type AffordabilityError } from '@domain/policies/BalancePolicy';
import { resolveEntitlement } from '@domain/policies/TenurePolicy';
import {
  describeBusinessDays,
  hasSufficientNotice,
  isBusinessDay,
  nextBusinessDay,
  noticeDaysGiven,
  type BusinessDayBreakdown,
} from '@domain/policies/BusinessDayPolicy';
import {
  assessOverlap,
  findSelfOverlap,
  type OccupiedSpan,
  type OverlapAssessment,
} from '@domain/policies/OverlapPolicy';
import { OCCUPYING_STATUSES } from '@domain/vocabulary';
import { CalendarDate } from '@domain/value-objects/CalendarDate';
import type { DateRange } from '@domain/value-objects/DateRange';
import type {
  IBalanceRepository,
  IClock,
  IHolidayRepository,
  IOrganizationRepository,
  IPersonLeaderRepository,
  IPersonRepository,
  IVacationRequestRepository,
  RepositoryError,
} from '../ports';
import { featureOn, requireAccess, type AccessDenial, type Session } from './session';

/**
 * Everything the request wizard needs to know about a proposed set of dates,
 * computed in one place.
 *
 * This is the use case the wizard re-runs as the user drags across the date
 * picker, and it is deliberately *advisory*: it reports conflicts, warnings and
 * costs without deciding anything. A user is allowed to submit a request that
 * breaches the sub area's cap or gives short notice — the design shows the
 * warning and lets a leader judge. Blocking submission outright would leave
 * someone with no way to ask, which is worse than being told no.
 *
 * The two things it does block are a range with no working days in it and days
 * the person provably does not have. Even the second is re-checked server-side at
 * approval, recomputed from the stored requests rather than the cached balance,
 * so a counter that lags can never grant days that are not there.
 */

export interface PrepareVacationRequestInput {
  readonly range: DateRange;
  /** Set when replacing a rejected request, so it is excluded from the overlap. */
  readonly replacesRequestFolio?: string | null;
}

export interface VacationRequestAssessment {
  readonly range: DateRange;
  readonly days: BusinessDayBreakdown;
  /** Null when the requester has no sub area, so there is no cap to assess. */
  readonly overlap: OverlapAssessment | null;
  /** Set when the requester already has an absence touching these dates. */
  readonly selfOverlap: OccupiedSpan | null;
  readonly balance: {
    readonly available: number;
    readonly afterThisRequest: number;
    readonly known: boolean;
    readonly affordable: boolean;
    readonly error: AffordabilityError | null;
    /**
     * De qué periodo sale cada parte, del que vence antes al que vence después.
     *
     * Con los periodos encimados «de dónde salen» deja de ser evidente: unas
     * mismas fechas pueden gastar lo que queda del periodo viejo y seguir con el
     * nuevo. `lastValidDay` es el último día en que ese periodo se puede gozar.
     */
    readonly shares: readonly BalanceShare[];
    /**
     * Días de un periodo vigente hoy que vence **antes** de las fechas elegidas,
     * y que esta solicitud por eso no puede gastar. Lo que hay que avisar.
     */
    readonly expiringUnused: BalanceShare | null;
    /**
     * El periodo en el que caen las fechas, cuando es **más nuevo** que el que
     * las paga. Sin esto, quien pide abril de 2027 con aniversario en marzo cree
     * que gasta su periodo 2027 cuando está gastando lo que queda del 2026.
     */
    readonly laterPeriod: { readonly year: number; readonly startsOn: string } | null;
    /**
     * Lo que le queda en cada periodo vigente en esas fechas **después** de esta
     * solicitud, del que vence antes al que vence después.
     *
     * Es el desglose de `afterThisRequest`: con dos periodos vivos una sola cifra
     * mezcla días que vencen en fechas distintas, y lo que la persona necesita
     * saber es cuántos le quedan de cada uno.
     */
    readonly after: readonly BalanceAfter[];
  };
  readonly notice: {
    readonly daysGiven: number;
    readonly required: number;
    readonly sufficient: boolean;
  };
  readonly requiredSignatures: SignatureCount;
  readonly approverNames: readonly string[];
  /** The first working day back, for "Regresas el …". */
  readonly returnsOn: string;
  /** Replacement is mandatory when the `reemplazo` feature is on. */
  readonly replacementRequired: boolean;
  readonly warnings: readonly RequestWarning[];
  /** True when nothing blocks submission. Warnings do not block. */
  readonly submittable: boolean;
}

export interface BalanceShare {
  readonly year: number;
  readonly days: number;
  /** ISO, inclusivo. */
  readonly lastValidDay: string;
  /** ISO. El aniversario que abre el periodo. */
  readonly startsOn: string;
  /**
   * El saldo de ese periodo todavía no existe y se calculó: el derecho que la
   * tabla le da al abrir. Es lo mismo que hará el servidor al crearlo.
   */
  readonly projected: boolean;
}

/** Lo que queda de un periodo después de la solicitud. */
export interface BalanceAfter {
  readonly year: number;
  readonly days: number;
  /** ISO, inclusivo. */
  readonly lastValidDay: string;
  /** ISO. El aniversario que lo abre. */
  readonly startsOn: string;
  readonly projected: boolean;
}

export type RequestWarning =
  | { readonly kind: 'cap-exceeded'; readonly days: readonly string[]; readonly cap: number }
  | { readonly kind: 'self-overlap'; readonly conflictingRange: string }
  | { readonly kind: 'short-notice'; readonly given: number; readonly required: number }
  | { readonly kind: 'holidays-deducted'; readonly dates: readonly string[] }
  | { readonly kind: 'no-business-days' }
  | {
      readonly kind: 'insufficient-balance';
      readonly requested: number;
      readonly available: number;
    }
  | { readonly kind: 'below-minimum'; readonly requested: number; readonly minimum: number }
  | { readonly kind: 'no-sub-area' };

export type PrepareError =
  | { readonly kind: 'access-denied'; readonly denial: AccessDenial }
  | { readonly kind: 'repository'; readonly error: RepositoryError };

export interface PrepareVacationRequestDeps {
  readonly clock: IClock;
  readonly requests: IVacationRequestRepository;
  readonly holidays: IHolidayRepository;
  readonly balances: IBalanceRepository;
  readonly people: IPersonRepository;
  readonly leaders: IPersonLeaderRepository;
  readonly organization: IOrganizationRepository;
}

/**
 * Absences read when assessing the cap. A sub area is a small unit — the largest
 * in the handoff has nine people — so one page covers any realistic window. The
 * limit exists so the query can never degrade into a full-collection scan.
 */
const OVERLAP_PAGE_SIZE = 100;

export const prepareVacationRequest = async (
  deps: PrepareVacationRequestDeps,
  session: Session,
  input: PrepareVacationRequestInput,
): Promise<Result<VacationRequestAssessment, PrepareError>> => {
  const denial = requireAccess(session, { permission: 'solicitar' });
  if (denial !== null) return err({ kind: 'access-denied', denial });

  const today = deps.clock.today();
  const { range } = input;
  const replacedFolio = input.replacesRequestFolio ?? null;
  const warnings: RequestWarning[] = [];

  // --- Business days -------------------------------------------------------
  // The holiday calendar participates only when `feriados` is on; an empty set is
  // how the flag reaches the calculation.
  const years = [...new Set([range.start.year, range.end.year])];
  const nonWorkingResult = featureOn(session, 'feriados')
    ? await deps.holidays.nonWorkingDates(years)
    : ok<ReadonlySet<string>>(new Set<string>());
  if (!nonWorkingResult.ok) return err({ kind: 'repository', error: nonWorkingResult.error });
  const nonWorking = nonWorkingResult.value;

  const days = describeBusinessDays(range, nonWorking);

  if (days.businessDays === 0) warnings.push({ kind: 'no-business-days' });
  if (days.deductedHolidays.length > 0) {
    warnings.push({ kind: 'holidays-deducted', dates: days.deductedHolidays });
  }

  // --- Concurrency cap, and self-overlap -----------------------------------
  let overlap: OverlapAssessment | null = null;
  let selfOverlap: OccupiedSpan | null = null;

  if (session.subAreaId === null) {
    warnings.push({ kind: 'no-sub-area' });
  } else {
    const peers = await deps.requests.list(
      // The area rides along so the security rules can prove the query; the sub
      // area is still what scopes it. See `constraintsFor` in the repository.
      {
        areaId: session.areaId,
        subAreaId: session.subAreaId,
        statuses: OCCUPYING_STATUSES,
        touching: range,
      },
      { limit: OVERLAP_PAGE_SIZE },
    );
    if (!peers.ok) return err({ kind: 'repository', error: peers.error });

    const relevant = peers.value.items.filter(
      (r) => replacedFolio === null || r.folio !== replacedFolio,
    );

    const toSpan = (r: (typeof relevant)[number]): OccupiedSpan => ({
      personId: r.personId,
      personName: r.personName,
      range: r.range,
      tentative: r.status !== 'Aprobada' && r.status !== 'Tomada',
    });

    // A person does not compete with themselves for their sub area's headroom, so
    // their own absences are excluded from the cap and assessed separately as a
    // double-booking mistake.
    const peerSpans = relevant.filter((r) => r.personId !== session.personId).map(toSpan);
    const ownSpans = relevant.filter((r) => r.personId === session.personId).map(toSpan);

    const capResult = await resolveCap(deps, session);
    if (!capResult.ok) return err({ kind: 'repository', error: capResult.error });

    overlap = assessOverlap(range, peerSpans, capResult.value);
    if (overlap.exceedsCap) {
      warnings.push({
        kind: 'cap-exceeded',
        days: overlap.conflictingDays.map((d) => d.date),
        cap: overlap.cap,
      });
    }

    selfOverlap = findSelfOverlap(range, ownSpans) ?? null;
    if (selfOverlap !== null) {
      warnings.push({ kind: 'self-overlap', conflictingRange: selfOverlap.range.toString() });
    }
  }

  // --- Balance -------------------------------------------------------------
  // A person in their first days has no balance document yet. That is not an
  // error: treat the entitlement as unknown, skip the affordability check, and
  // let the server decide at approval. Failing hard here would block a legitimate
  // first request.
  /*
   * **Pagan primero los días que vencen antes**, igual que en el servidor.
   *
   * Hasta sep 2026 cada día lo pagaba el periodo en cuyo año caía, y «Vencimiento
   * del saldo» no gobernaba nada: con 18 meses configurados, quien cumplía el 16
   * de marzo y pedía abril se encontraba el periodo viejo cerrado aunque sus días
   * valían hasta el 16 de septiembre del año siguiente. Ahora los periodos se
   * enciman: cada uno vive desde su aniversario hasta N meses después, y cada día
   * hábil lo paga el más viejo que siga vigente y le quede saldo. Ver
   * `allocateAcrossPeriods` y `livePeriodsForRange` del servidor, que es el que
   * decide.
   */
  const expiryMonths = session.config.rules.expiryMonths;
  const periodosDelRango =
    session.hireDate === null
      ? calendarYearsBetween(range.start.year, range.end.year)
      : livePeriodsBetween(session.hireDate, range.start, range.end, expiryMonths).map((p) => ({
          year: p.year,
          start: p.start,
          validUntil: validUntil(p, expiryMonths),
        }));

  /* Los que vencen antes de las fechas elegidas, para avisar que se pierden. */
  const periodosDeHoy =
    session.hireDate === null
      ? []
      : livePeriodsOn(session.hireDate, today, expiryMonths)
          .filter((p) => !periodosDelRango.some((q) => q.year === p.year))
          .map((p) => ({ year: p.year, start: p.start, validUntil: validUntil(p, expiryMonths) }));

  /*
   * Un periodo sin documento de saldo, con la misma regla que el servidor.
   *
   * - **Del que corre en adelante** se proyecta: el derecho que la tabla le da al
   *   abrir —o el de contrato— menos la deuda que arrastre el anterior. Es
   *   exactamente lo que hará `ensureBalance` al crearlo, así que la cuenta que
   *   se enseña es la que se va a cobrar. Sin esto, quien pide abril de 2027 con
   *   aniversario en marzo leía «Saldo después: —»: su periodo 2027 no existe
   *   hasta que abre.
   * - **Uno anterior** cuenta cero: el servidor ya no inventa esos periodos
   *   —nunca se importaron—, así que aquí tampoco pagan.
   */
  const inicioActual =
    session.hireDate === null ? null : periodFor(session.hireDate, today).start;
  let contrato: number | null | undefined;
  const derechoPorContrato = async (): Promise<number | null> => {
    if (contrato !== undefined) return contrato;
    const persona = await deps.people.getById(session.personId);
    contrato = persona.ok ? persona.value.entitledDaysOverride : null;
    return contrato;
  };
  const proyectar = async (p: {
    year: number;
    start: CalendarDate;
  }): Promise<{ available: number; projected: boolean } | null> => {
    if (session.hireDate === null || inicioActual === null) return null;
    if (p.start.isBefore(inicioActual)) return { available: 0, projected: false };
    const deContrato = await derechoPorContrato();
    const derecho =
      deContrato ??
      resolveEntitlement({
        hireDate: session.hireDate,
        asOf: p.start,
        seniority: session.config.rules.seniority,
        legalMinimumDays: session.config.rules.legalMinimumDays,
      }).days;
    const previo = await deps.balances.get(session.personId, p.year - 1);
    const neto = previo.ok
      ? previo.value.entitledDays -
        previo.value.openingUsedDays -
        previo.value.takenDays -
        previo.value.scheduledDays -
        previo.value.pendingDays
      : 0;
    return { available: derecho - Math.max(0, -neto), projected: true };
  };

  const leidos = await Promise.all(
    [...periodosDelRango, ...periodosDeHoy].map(async (p, indice) => {
      const saldo = await deps.balances.get(session.personId, p.year);
      if (saldo.ok) {
        return { ...p, known: true, projected: false, available: availableDays(saldo.value) };
      }
      /* Sólo se proyecta lo que paga estas fechas; los de hoy que vencen antes
         necesitan el dato real para avisar de una pérdida. */
      const proyectado = indice < periodosDelRango.length ? await proyectar(p) : null;
      return proyectado === null
        ? { ...p, known: false, projected: false, available: 0 }
        : { ...p, known: true, ...proyectado };
    }),
  );
  const delRango = leidos.slice(0, periodosDelRango.length);
  const deHoy = leidos.slice(periodosDelRango.length);

  const allocation = allocateAcrossPeriods(
    range.start,
    range.end,
    delRango.filter((p) => p.known),
    (day) => isBusinessDay(day, nonWorking),
  );

  /*
   * Un periodo sin documento de saldo —uno que todavía no empieza, o un alta
   * reciente— se reparte como si tuviera cero. Si con eso alcanza, la cuenta es
   * buena; si no alcanza, no se puede saber si ese periodo lo habría cubierto, y
   * decide el servidor: no poder adelantar la cuenta es mejor que adelantarla mal.
   */
  const algunoDesconocido = delRango.some((p) => !p.known) || delRango.length === 0;
  const balanceKnown = allocation.shortfall === 0 || !algunoDesconocido;
  const available = delRango.reduce((suma, p) => suma + Math.max(0, p.available), 0);
  const cubiertos = days.businessDays - allocation.shortfall;

  /*
   * El **mínimo por solicitud** es del total: pedir medio día sigue siendo medio
   * día aunque lo paguen dos periodos. La **suficiencia** es lo que el reparto no
   * pudo cubrir, que no es lo mismo que la suma de saldos: un periodo que todavía
   * no abre no paga los días de antes de su aniversario.
   */
  const affordability = balanceKnown
    ? canAfford({
        requestedBusinessDays: days.businessDays,
        available: cubiertos,
        minimumRequestDays: session.config.rules.minimumRequestDays,
      })
    : ({ ok: true } as const);

  if (!affordability.ok) {
    const { error } = affordability;
    if (error.kind === 'insufficient') {
      warnings.push({
        kind: 'insufficient-balance',
        requested: error.requested,
        available: error.available,
      });
    } else if (error.kind === 'below-minimum') {
      warnings.push({
        kind: 'below-minimum',
        requested: error.requested,
        minimum: error.minimum,
      });
    }
  }

  const ultimoDia = (p: { validUntil: CalendarDate }): string => p.validUntil.addDays(-1).toISO();

  const periodoDeLasFechas =
    session.hireDate === null ? null : periodFor(session.hireDate, range.end);
  const primeroQuePaga = allocation.shares[0];
  /* Sólo cuando ese periodo anterior paga la solicitud entera: si el nuevo pone
     una parte, lo que hay que decir es el reparto, y ése es otro aviso. */
  const laterPeriod =
    periodoDeLasFechas !== null &&
    primeroQuePaga !== undefined &&
    allocation.shares.length === 1 &&
    primeroQuePaga.days === days.businessDays &&
    primeroQuePaga.year < periodoDeLasFechas.year
      ? { year: periodoDeLasFechas.year, startsOn: periodoDeLasFechas.start.toISO() }
      : null;

  const porVencer = deHoy
    .filter((p) => p.known && p.available > 0 && !p.validUntil.isAfter(range.start))
    .sort((a, b) => a.year - b.year)[0];

  // --- Notice period -------------------------------------------------------
  const requiredNotice = session.config.rules.noticeDays;
  const givenNotice = noticeDaysGiven(today, range.start);
  const noticeSufficient = hasSufficientNotice(today, range.start, requiredNotice);
  if (!noticeSufficient) {
    warnings.push({ kind: 'short-notice', given: givenNotice, required: requiredNotice });
  }

  // --- Approval chain ------------------------------------------------------
  const leaderLinks = await deps.leaders.leadersOf(session.personId, session.areaId);
  if (!leaderLinks.ok) return err({ kind: 'repository', error: leaderLinks.error });

  const leaderIds: readonly PersonId[] = leaderLinks.value.map((l) => l.leaderPersonId);
  const leaderPeople = await deps.people.getMany(leaderIds);
  const approverNames = leaderPeople.ok ? leaderPeople.value.map((p) => p.displayName) : [];

  const requiredSignatures = requiredSignaturesFor({
    leaderCount: leaderIds.length,
    doubleSignOffEnabled: featureOn(session, 'doblevb'),
  });

  return ok({
    range,
    days,
    overlap,
    selfOverlap,
    balance: {
      available,
      afterThisRequest: Math.max(0, available - cubiertos),
      known: balanceKnown,
      affordable: affordability.ok,
      error: affordability.ok ? null : affordability.error,
      shares: allocation.shares.map((share) => {
        const periodo = delRango.find((p) => p.year === share.year);
        return {
          year: share.year,
          days: share.days,
          lastValidDay: periodo === undefined ? share.to.toISO() : ultimoDia(periodo),
          startsOn: periodo?.start.toISO() ?? share.from.toISO(),
          projected: periodo?.projected ?? false,
        };
      }),
      after: balanceKnown
        ? delRango.map((periodo) => ({
            year: periodo.year,
            days: Math.max(
              0,
              periodo.available -
                (allocation.shares.find((share) => share.year === periodo.year)?.days ?? 0),
            ),
            lastValidDay: ultimoDia(periodo),
            startsOn: periodo.start.toISO(),
            projected: periodo.projected,
          }))
        : [],
      expiringUnused:
        porVencer === undefined
          ? null
          : {
              year: porVencer.year,
              days: porVencer.available,
              lastValidDay: ultimoDia(porVencer),
              startsOn: porVencer.start.toISO(),
              projected: false,
            },
      laterPeriod,
    },
    notice: { daysGiven: givenNotice, required: requiredNotice, sufficient: noticeSufficient },
    requiredSignatures,
    approverNames,
    returnsOn: nextBusinessDay(range.end, nonWorking).toISO(),
    replacementRequired: featureOn(session, 'reemplazo'),
    warnings,
    submittable: days.businessDays > 0 && affordability.ok,
  });
};

/**
 * The requester's sub area cap.
 *
 * Falls back to the company default when the sub area cannot be read or has
 * never had a cap set — a missing value must not read as zero, which would flag
 * every single request as a breach.
 */
const resolveCap = async (
  deps: PrepareVacationRequestDeps,
  session: Session,
): Promise<Result<number, RepositoryError>> => {
  const fallback = session.config.rules.defaultMaxConcurrentOut;
  if (session.subAreaId === null) return ok(fallback);

  const subArea = await deps.organization.getSubArea(session.subAreaId);
  if (!subArea.ok) {
    // A sub area that has been deleted while a wizard was open is not worth
    // failing the whole assessment over; the configured default is a safe floor.
    return subArea.error.kind === 'not-found' ? ok(fallback) : err(subArea.error);
  }

  return ok(subArea.value.maxConcurrentOut);
};

/**
 * Sin fecha de ingreso no hay aniversario: año natural y sin encimado, lo mismo
 * que hace `livePeriodsForRange` en el servidor.
 */
const calendarYearsBetween = (
  from: number,
  to: number,
): readonly { year: number; start: CalendarDate; validUntil: CalendarDate }[] => {
  const years = [];
  for (let year = from; year <= to; year += 1) {
    years.push({
      year,
      start: CalendarDate.fromISOOrThrow(`${String(year)}-01-01`),
      validUntil: CalendarDate.fromISOOrThrow(`${String(year + 1)}-01-01`),
    });
  }
  return years;
};
